import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Controller, useFormContext } from "react-hook-form"
import { FormLabel, Select, TextArea } from "@oysterhr/ui"
import { Engagement, EngagementType, ExchangeRateTypeEnum } from "@oysterhr/schema"
import { DoubleMoneyInput } from "../../../DoubleMoneyInput"
import { useOneTimePayrollChange } from "./hooks/useOneTimePayrollChange"
import { useRecurringPayrollChange } from "./hooks/useRecurringPayrollChange"
import { AvailablePaySupplementLabels, PaymentSchedule } from "./constants"
import { useOysterCurrencyConversion } from "@oysterhr/common"
import { PayrollChangeFormValues } from "./types"
import { TeamMemberAsyncSelect } from "./components/TeamMemberAsyncSelect"
import { PaymentScheduleFields } from "./components/PaymentScheduleFields"
import { useSetPaymentCurrencyCode } from "./hooks/useSetPaymentCurrencyCode"
import { HighAmountSupplementAlert } from "../../../PaySupplementFormPage/components/PaySupplementsForm/components/HighSupplementAmountAlert"
import { PayrollChangeFTERestricted } from "../PayrollChangeFTERestricted"
import { PayrollContext } from "../../../../contexts/PayrollContext/PayrollContext"
import { useAuthorized } from "@oysterhr/auth"

interface Props {
  externalEngagementId?: string
  isTeamMemberReadonly?: boolean
  isDateReadOnly?: boolean
  onSuccess: () => void
  close: () => void
  setIsFTE: Dispatch<SetStateAction<boolean>>
}

export function AddPayrollChangeForm({
  externalEngagementId,
  isTeamMemberReadonly = false,
  isDateReadOnly = false,
  onSuccess,
  close,
  setIsFTE,
}: Props) {
  const { t } = useTranslation("Payroll")
  const { t: paySupplementTranslation } = useTranslation("PaySupplements")
  const [engagement, setEngagement] = useState<Engagement>()

  const onOneTimePayrollChangeSubmit = useOneTimePayrollChange({ onSuccess, close })
  const onRecurringPayrollChangeSubmit = useRecurringPayrollChange({ onSuccess, close })
  const { control, handleSubmit, watch, setValue } = useFormContext<PayrollChangeFormValues>()
  const { company } = useContext(PayrollContext)
  const { isAuthorized } = useAuthorized({
    authorizations: company?.paymentsSettings?.authorizations,
  })

  useSetPaymentCurrencyCode({ setValue })
  const isFTE = useMemo(
    () =>
      engagement?.engagementType === EngagementType.Employment && !engagement?.isPayrollTeamMember,
    [engagement],
  )
  useEffect(() => {
    setIsFTE(isFTE)
  }, [isFTE, setIsFTE])

  const paymentCurrencyCode = watch("paymentCurrencyCode")
  const amount = watch("amount")
  const amountCurrencyCode =
    engagement?.employment?.salaryCurrencyCode || engagement?.contract?.salaryCurrencyCode
  const paymentTypeOptions = useMemo(
    () =>
      Object.entries(AvailablePaySupplementLabels)
        .map(([value, translationKey]) => ({
          value,
          label: paySupplementTranslation(translationKey),
        }))
        .sort(({ label: a }, { label: b }) => a.localeCompare(b)),
    [paySupplementTranslation],
  )
  const onSubmit = useCallback(
    (values: PayrollChangeFormValues) =>
      values.paymentSchedule === PaymentSchedule.OneTime
        ? onOneTimePayrollChangeSubmit(values)
        : onRecurringPayrollChangeSubmit(values),
    [onOneTimePayrollChangeSubmit, onRecurringPayrollChangeSubmit],
  )
  const exchangeRateType =
    engagement?.engagementType === EngagementType.Contract
      ? ExchangeRateTypeEnum.Contractors
      : ExchangeRateTypeEnum.FullTimeEmployees
  const { getReverseFxConversionRate } = useOysterCurrencyConversion(exchangeRateType)
  const isRestricted =
    !isAuthorized(["update_restriction"]) && company?.paymentsSettings?.restricted && isFTE

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      id="add-payroll-change-form"
    >
      <TeamMemberAsyncSelect
        setEngagement={setEngagement}
        disabled={isTeamMemberReadonly}
        externalEngagementId={externalEngagementId}
      />
      {isRestricted ? (
        <PayrollChangeFTERestricted />
      ) : (
        <>
          <div className="flex flex-col gap-4 rounded-lg bg-gray-100 p-5">
            <Controller
              name="paymentType"
              control={control}
              render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
                <Select
                  label={<FormLabel label={t("form.label.paymentType")} htmlFor={field.name} />}
                  error={error?.message}
                  placeholder={t("form.placeholders.paymentType")}
                  {...field}
                >
                  {paymentTypeOptions.map(({ label, value }) => (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            />
            {amountCurrencyCode && (
              <DoubleMoneyInput
                control={control}
                officialCurrency={amountCurrencyCode}
                approximateCurrency={paymentCurrencyCode}
                officialFieldName="amount"
                approximateFieldName="approximateAmount"
                getCurrencyConversionFactor={getReverseFxConversionRate}
              />
            )}
            <HighAmountSupplementAlert engagement={engagement} amount={amount} />
            <PaymentScheduleFields engagement={engagement} isDateReadOnly={isDateReadOnly} />
          </div>
          <Controller
            control={control}
            name="notes"
            render={({ field, fieldState: { error } }) => (
              <TextArea
                {...field}
                name="notes"
                label={<FormLabel label={t("form.label.notes")} isOptional />}
                placeholder={t("form.placeholders.notes")}
                error={error?.message}
              />
            )}
          />
        </>
      )}
    </form>
  )
}
